/**
 * 分级选择组件
 * Cascading Select Component
 * 支持按分类进行两级选择，提升大量数据时的选择体验
 */
import React, { useMemo } from 'react';
import { Cascader, Space, Tag, Typography } from 'antd';
import type { CascaderProps, DefaultOptionType } from 'antd/es/cascader';

const { Text } = Typography;

export interface CascadingSelectOption {
  name: string;
  category: string;
  unit?: string;
  specifications?: string;
}

interface CascadingSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  options: CascadingSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  categoryLabels?: Record<string, string>;
  showSearch?: boolean;
  allowClear?: boolean;
}

/**
 * 默认分类标签映射
 */
const defaultMaterialCategoryLabels: Record<string, string> = {};

const defaultEquipmentCategoryLabels: Record<string, string> = {};

const CascadingSelect: React.FC<CascadingSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = '请选择',
  disabled = false,
  style,
  categoryLabels,
  showSearch = true,
  allowClear = true,
}) => {
  // 将扁平数据转换为级联数据结构
  const cascaderOptions = useMemo(() => {
    // 按分类分组
    const categoryMap = new Map<string, CascadingSelectOption[]>();
    
    options.forEach(item => {
      const category = item.category || '其他';
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(item);
    });

    // 合并默认标签和自定义标签
    const labels = {
      ...defaultMaterialCategoryLabels,
      ...defaultEquipmentCategoryLabels,
      ...categoryLabels,
    };

    // 转换为级联选项格式
    const result: DefaultOptionType[] = [];
    
    categoryMap.forEach((items, category) => {
      const categoryLabel = labels[category] || category;
      
      result.push({
        value: category,
        label: (
          <Space>
            <span>{categoryLabel}</span>
            <Tag color="blue" style={{ fontSize: 10 }}>{items.length}</Tag>
          </Space>
        ),
        children: items.map(item => ({
          value: item.name,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{item.name}</span>
              {item.unit && (
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  {item.unit}
                </Text>
              )}
            </div>
          ),
          // 存储原始数据用于搜索
          searchValue: `${item.name} ${item.specifications || ''} ${category}`,
        })),
      });
    });

    // 按分类名称排序
    result.sort((a, b) => {
      const labelA = typeof a.label === 'string' ? a.label : String(a.value);
      const labelB = typeof b.label === 'string' ? b.label : String(b.value);
      return labelA.localeCompare(labelB, 'zh-CN');
    });

    return result;
  }, [options, categoryLabels]);

  // 将当前值转换为级联值格式 [category, name]
  const cascaderValue = useMemo(() => {
    if (!value) return undefined;
    
    // 查找该值所属的分类
    const item = options.find(opt => opt.name === value);
    if (item) {
      return [item.category || '其他', value];
    }
    return undefined;
  }, [value, options]);

  // 处理选择变化
  const handleChange: CascaderProps<DefaultOptionType>['onChange'] = (selectedValue) => {
    if (selectedValue && selectedValue.length === 2) {
      // 选择了具体项目，返回名称
      onChange?.(selectedValue[1] as string);
    } else {
      onChange?.('');
    }
  };

  // 搜索过滤函数
  const filter: CascaderProps<DefaultOptionType>['showSearch'] = showSearch ? {
    filter: (inputValue, path) => {
      return path.some(option => {
        const searchValue = (option as any).searchValue || String(option.value);
        return searchValue.toLowerCase().includes(inputValue.toLowerCase());
      });
    },
    matchInputWidth: false,
    limit: 50,
  } : false;

  // 显示渲染函数
  const displayRender = (labels: string[], selectedOptions?: DefaultOptionType[]) => {
    if (selectedOptions && selectedOptions.length === 2) {
      // 只显示最终选择的名称
      return selectedOptions[1].value as string;
    }
    return labels.join(' / ');
  };

  return (
    <Cascader
      value={cascaderValue}
      onChange={handleChange}
      options={cascaderOptions}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: '100%', ...style }}
      showSearch={filter}
      allowClear={allowClear}
      displayRender={displayRender}
      expandTrigger="hover"
      popupClassName="cascading-select-popup"
    />
  );
};

export default CascadingSelect;
